import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSharedItem } from '../services/sharing';
import PasswordEntryModal from '../components/PasswordEntryModal';
import DesktopWarningBanner from '../components/DesktopWarningBanner';
import { RefreshCcw } from 'lucide-react';

// Reuse the injectScrollbarStyles function from other view components
const injectScrollbarStyles = (htmlContent) => {
  const scrollbarStyles = `
    <style>
      ::-webkit-scrollbar {
        width: 6px;
      }
      
      ::-webkit-scrollbar-track {
        background: transparent;
      }
      
      ::-webkit-scrollbar-thumb {
        background-color: rgba(100, 100, 100, 0.5);
        border-radius: 4px;
      }
      
      ::-webkit-scrollbar-thumb:hover {
        background-color: rgba(100, 100, 100, 0.7);
      }
      
      * {
        scrollbar-width: thin;
        scrollbar-color: rgba(100, 100, 100, 0.5) transparent;
      }
    </style>
  `;

  if (htmlContent.includes('<head>')) {
    return htmlContent.replace('<head>', '<head>' + scrollbarStyles);
  } else {
    return htmlContent.replace('<html>', '<html><head>' + scrollbarStyles + '</head>');
  }
};

/* ---------- rate‑limit reporter & navigation interceptor ---------- */
const injectRateLimitReporter = (htmlContent) => {
  const reporterScript = `
    <script>
      (function(){
        // Existing rate limit reporting logic
        const API_MARKER = '/api/v1/images/';
        window.addEventListener('error', function(e){
          const img = e.target;
          if (img && img.tagName === 'IMG' && img.src && img.src.includes(API_MARKER)) {
            fetch(img.src, { method: 'HEAD', cache: 'no-store' })
              .then(r => {
                if (r.status === 429) {
                  parent.postMessage({ type: 'IMAGE_RATE_LIMIT', src: img.src }, '*');
                }
              })
              .catch(()=>{});
          }
        }, true);

        // NEW: Intercept link clicks to prevent iframe navigation
        document.addEventListener('click', function(e) {
          const link = e.target.closest('a');
          // Only intercept real links, not in-page anchor links
          if (link && link.href && !link.href.startsWith('#')) {
            e.preventDefault();
            e.stopPropagation();
            parent.postMessage({ type: 'LINK_CLICK', href: link.href }, '*');
          }
        }, true);
        
        // NEW: Intercept form submissions
        document.addEventListener('submit', function(e) {
          e.preventDefault();
          e.stopPropagation();
          parent.postMessage({ type: 'FORM_SUBMIT', action: e.target.action }, '*');
        }, true);
      })();
    </script>
  `;
  return htmlContent.includes('</body>')
    ? htmlContent.replace('</body>', reporterScript + '</body>')
    : htmlContent + reporterScript;
};

const SharedContent = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [sharedData, setSharedData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [viewLimitExceeded, setViewLimitExceeded] = useState(false);
  const [showOopsMessage, setShowOopsMessage] = useState(false); // New state for link clicks
  
  // For flow navigation
  const goToNextPage = () => {
    if (sharedData?.data?.pages && currentPage < sharedData.data.pages.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Wrap loadSharedContent with useCallback to prevent infinite loops
  const loadSharedContent = useCallback(async (password = null) => {
    if (!shareId) return;
    
    setLoading(true);
    setError(null);
    setPasswordError(false);
    setViewLimitExceeded(false);
    
    try {
      const response = await getSharedItem(shareId, password);
      
      if (response.status === 'password_required') {
        setRequiresPassword(true);
        setLoading(false);
        return;
      }
      
      if (response.status === 'incorrect_password') {
        setRequiresPassword(true);
        setPasswordError(true);
        setLoading(false);
        return;
      }
      
      if (response.status === 'error' && response.code === 'view_limit_exceeded') {
        setViewLimitExceeded(true);
        setLoading(false);
        return;
      }
      
      setSharedData(response);
      setRequiresPassword(false);
    } catch (err) {
      if (err.response?.data?.code === 'view_limit_exceeded') {
        setViewLimitExceeded(true);
      } else {
        console.error('Error loading shared content:', err);
        setError('This content is no longer available or has expired.');
      }
    } finally {
      setLoading(false);
    }
  }, [shareId]);
  
  useEffect(() => {
    if (shareId) {
      loadSharedContent();
    }
  }, [shareId, loadSharedContent]);
  
  // All fullscreen-like container styling
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.margin = '';
      document.body.style.padding = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.margin = '';
      document.documentElement.style.padding = '';
    };
  }, []);

  // NEW: Listen for messages from the iframe (e.g., intercepted link clicks)
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && (event.data.type === 'LINK_CLICK' || event.data.type === 'FORM_SUBMIT')) {
        setShowOopsMessage(true);
      }
    };
  
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);
  
  const handlePasswordSubmit = (password) => {
    loadSharedContent(password);
  };
  
  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">Loading shared content...</p>
        </div>
      </div>
    );
  }
  
  if (requiresPassword) {
    return (
      <PasswordEntryModal
        onSubmit={handlePasswordSubmit}
        onCancel={() => navigate('/')}
        error={passwordError ? 'Incorrect password. Please try again.' : null}
      />
    );
  }

  if (viewLimitExceeded) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <p className="text-lg font-semibold">View Limit Exceeded</p>
          </div>
          <p className="text-gray-700 mb-6">
            This shared content has reached its maximum view limit (200 views).
            Please contact the content owner for a new share link if needed.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
            <p className="text-lg font-semibold">Error</p>
          </div>
          <p className="text-gray-700 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // NEW: Show the "Oops" message if a link was clicked in the iframe
  if (showOopsMessage) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Oops! That Didn't Work</h2>
          <p className="text-gray-600 mb-6">
            Looks like the action you chose could not be accomplished. Click below to go back.
          </p>
          <button
            onClick={() => setShowOopsMessage(false)}
            className="px-6 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors flex items-center justify-center space-x-2 mx-auto"
          >
            <RefreshCcw size={20} />
            <span>Go Back</span>
          </button>
        </div>
      </div>
    );
  }
  
  if (!sharedData) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-white">No content available</p>
        </div>
      </div>
    );
  }
  
  // Handle shared view
  if (sharedData.itemType === 'view') {
    const view = sharedData.data;
    
    const currentVersion = view.isVersionFiltered 
      ? view.versions[0] 
      : view.versions[view.versions.length - 1];
      
    const device = view.device || 'Desktop';
    const isDesktop = device === 'Desktop';
    
    return (
      <div className="fixed inset-0 flex flex-col bg-black">
        <DesktopWarningBanner isDesktop={isDesktop} />
        <div className="bg-neutral-900 py-2 px-4 flex justify-between items-center">
          <div className="text-sm font-bold text-slate-300">{view.title || 'Shared View'}</div>
          <div></div>
        </div>
        
        {isDesktop ? (
          <div className="flex-1 bg-white">
            <iframe 
              title="Shared View"
              srcDoc={injectRateLimitReporter(
                injectScrollbarStyles(currentVersion.html)
              )}
              style={{ width: "100%", height: "100%", border: "none" }}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-presentation"
            />
          </div>
        ) : (
          <div className="flex-1 bg-black flex items-center justify-center">
            <div className="border rounded-md shadow-sm p-4 bg-white mx-auto w-[384px]">
              <iframe 
                title="Shared View"
                srcDoc={injectRateLimitReporter(
                  injectScrollbarStyles(currentVersion.html)
                )}
                style={{ width: "100%", height: "80vh", border: "none" }}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-presentation"
              />
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Handle shared flow
  if (sharedData.itemType === 'flow') {
    const flow = sharedData.data;
    const pages = flow.pages || [];
    const currentPageData = pages[currentPage] || {};
    const device = flow.device || 'Desktop';
    const isDesktop = device === 'Desktop';
    
    return (
      <div className="fixed inset-0 flex flex-col bg-black">
        <DesktopWarningBanner isDesktop={isDesktop} />
        <div className="bg-neutral-900 py-2 px-4 flex justify-between items-center">
          <div className="text-sm font-bold text-slate-300">{flow.title || 'Shared Flow'}</div>
          
          <div className="flex gap-2 items-center">
            <button
              onClick={goToPrevPage}
              disabled={currentPage === 0}
              className="p-1 bg-slate-700 text-white rounded text-xs disabled:opacity-50"
            >
              Prev
            </button>
            
            <button
              onClick={goToNextPage}
              disabled={currentPage === pages.length - 1}
              className="p-1 bg-slate-700 text-white rounded text-xs disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
        
        {isDesktop ? (
          <div className="flex-1 bg-white">
            <iframe 
              title={`Flow Page ${currentPage + 1}`}
              srcDoc={injectRateLimitReporter(
                injectScrollbarStyles(
                  currentPageData.html || '<html><body><p>No content</p></body></html>'
                )
              )}
              style={{ width: "100%", height: "100%", border: "none" }}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-presentation"
            />
          </div>
        ) : (
          <div className="flex-1 bg-black flex items-center justify-center">
            <div className="border rounded-md shadow-sm p-4 bg-white mx-auto w-[384px]">
              <iframe 
                title={`Flow Page ${currentPage + 1}`}
                srcDoc={injectRateLimitReporter(
                  injectScrollbarStyles(
                    currentPageData.html || '<html><body><p>No content</p></body></html>'
                  )
                )}
                style={{ width: "100%", height: "80vh", border: "none" }}
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-modals allow-presentation"
              />
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      <div className="text-center">
        <p className="text-white">Unsupported content type</p>
      </div>
    </div>
  );
};

export default SharedContent;